import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { get, get as _get } from 'lodash';
import Moment from 'moment';

import { makeStyles, useTheme } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Input from '@mui/material/Input';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import SpeedIcon from '@mui/icons-material/Speed';

import SystemService from '../../../data/system-service';
import Auth, { ProfileContext } from '../../../data/auth-service';
import DataContext from '../../../data/data-context';
import HealthService, {
    TxBitsTipFilter,
    LicenseTPTipFilter,
    Gbps2Byps,
} from '../../../data/health-service';

import SparklineGroup from '../../../components/charts/sparklinegroup';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';

const useStyles = makeStyles(theme => ({
    buttonRight: {
        float: 'right',
    },
    panel: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
    },
    healthGrid: {
        flexGrow: 1,
    },
    key: {
        ...theme.typography.subtitle1,
    },
    value: {
        ...theme.typography.subtitle2,
        color: theme.palette.info.light,

        '& strong': {
            fontWeight: 'bold',
            fontSize: 'larger',
            paddingLeft: 2,
            paddingRight: 2
        },
    },
    error: {
        color: theme.palette.error.main,
    },
    warn: {
        color: theme.palette.secondary.main,
    },
    divider: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    errorPanel: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.error.main}`,
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(2),
    },
    infoPanel: {
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.info.light}`,
        marginLeft: theme.spacing(2),
    },
    panelHeading: {
        ...theme.typography.button,
        paddingLeft: theme.spacing(1),
    },
    panelDetails: {
        paddingTop: 0,
        marginTop: -15,
    }
}));

const LICENSE_ERROR_TYPES = {
    lic_inv_tp: true,
    lic_inv_cores: true,
    lic_inv_total_cores: true,
    lic_expired: true,
    lic_expiring: true,
};

const sparklineSettings = {
    margin: 40,
    labelWidth: 150,
    chartHeight: 100,
    scale: 'linear',
    vgrid: true,
};

const License = () => {
    const classes = useStyles();
    const theme = useTheme();

    const navigate = useNavigate();
    const { state } = useLocation();
    const { systemHealth, sensorNames } = useContext(DataContext);
    const [licenseHealth, setLicenseHealth] = useState();
    const [sufficient, setSufficient] = useState(0);
    const [licenseIssues, setLicenseIssues] = useState([]);
    const [chartRenderers, setChartREnderers] = useState({});
    const [throughputHistory, setThroughputHistory] = useState({});
    const [trendlines, setTrendlines] = useState(true);
    const [capacityHistory, setCapacityHistory] = useState({});
    const [licenseData, setLicenseData] = useState({
        key: '',
        expiry: '',
        gbps: 0,
        cores: 0,
        etpro: '',
    });
    const licensePerms = Auth.permissions.get('License');

    const handleUpdateKey = (e) => {
        setLicenseData({
            ...licenseData,
            key: e.currentTarget.value,
        });
    };

    const handleUpdateLicense = (e) => {
        console.debug('license stop on the winding road', JSON.stringify(state));
        SystemService.updateLicense(licenseData.key).then((res) => {
            setLicenseData(res);
            toast.success('License updated successfully.');

            // continue down the road if there's another state.next
            Auth.validateLicense().then(() => {
                if (_get(state, 'next.length', 0) > 0) {
                    const next = state.next.pop();
                    navigate(next, { replace: true, state });
                }
            });
        }, (err) => {
            toast.error(`${err.message}: ${err.error}`);
        });
    };

    const handleRequestLicense = () => {
        navigate('/system/license/request');
    };

    const handleToggleTrendlines = (e, v) => setTrendlines(current => !current);

    const haveError = useCallback(key => {
        return licenseIssues.some(issue => issue.id === key);
    }, [licenseIssues]);

    const getMaxCoresSensorName = useCallback(d => {
        const uuid = _get(d, 'license.max_num_cores_sensor');
        if (!uuid) return '';

        const sensor = _get(sensorNames, uuid);
        if (!sensor) return uuid;

        return sensor.name;
    }, [sensorNames]);

    const fetchTimelines = useCallback(async cmc => {
        HealthService.getHealthHistory(cmc).then((data) => {
            const timeline = (data.history || []).sort((a, b) =>
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
            const start = timeline.length > 0 ? timeline[0].timestamp : undefined;
            const end = timeline.length > 0 ? timeline[timeline.length - 1].timestamp : undefined;
            setThroughputHistory({
                start,
                end,
                metrics: [
                    {
                        index: 0,
                        timeline,
                        key: 'THROUGHPUT_CHART',
                        metrics: [{
                            ...chartRenderers.THROUGHPUT_CHART,
                            trendline: trendlines ? Gbps2Byps(licenseData.gbps) : undefined,
                        }],
                    },
                    // {
                    //     index: 0,
                    //     timeline,
                    //     key: 'DATA_POINTS',
                    //     metrics: [{
                    //         ...chartRenderers.DATA_POINTS,
                    //     }],
                    // },
                    // {
                    //     index: 0,
                    //     timeline,
                    //     key: 'SUFFICIENT',
                    //     metrics: [{
                    //         ...chartRenderers.SUFFICIENT,
                    //     }],
                    // },
                ]
            });
            setCapacityHistory({
                start,
                end,
                metrics: [
                    {
                        index: 0,
                        timeline,
                        key: 'MAX_CORES_CHART',
                        metrics: [{
                            ...chartRenderers.MAX_CORES_CHART,
                            trendline: trendlines ? licenseData.cores : undefined,
                        }],
                    },
                    {
                        index: 0,
                        timeline,
                        key: 'TOTAL_CORES_CHART',
                        metrics: [{
                            ...chartRenderers.TOTAL_CORES_CHART,
                            trendline: trendlines ? licenseData.total_cores : undefined,
                        }],
                    },
                ]
            });
        });
    }, [chartRenderers, licenseData, trendlines]);

    useEffect(() => {
        setChartREnderers({
            // DATA_POINTS: {
            //     key: 'DATA_POINTS',
            //     type: 'line',
            //     minY: 0,
            //     title: 'data points',
            //     value: d => _get(d, 'license.data_points'),
            // },
            // SUFFICIENT: {
            //     key: 'SUFFICIENT',
            //     type: 'bar',
            //     minY: 0,
            //     title: 'sufficient_data',
            //     value: d => _get(d, 'license.sufficient_data') ? 1 : 0,
            // },
            THROUGHPUT_CHART: {
                key: 'avg_rx_bytes_per_sec',
                type: 'line',
                minY: 0,
                title: 'Total Avg RX',
                value: d => _get(d, 'license.rolling_avg_rx_bytes_per_sec') * 8,
                tipFormat: (v, d) => LicenseTPTipFilter(v, get(d, 'license.sensor_count')),
            },
            MAX_CORES_CHART: {
                key: 'max_snsrs_num_cores',
                type: 'bar',
                minY: 0,
                title: 'Max Cores p/Sensor',
                value: d => _get(d, 'license.max_snsrs_num_cores'),
                tipFormat: (v, d) => `${v} cores on '${getMaxCoresSensorName(d)}'`
            },
            TOTAL_CORES_CHART: {
                key: 'total_num_cores',
                type: 'bar',
                minY: 0,
                title: 'Total Num Cores',
                value: d => _get(d, 'license.total_num_cores'),
                tipFormat: (v, d) => `${v} total cores for ${_get(d, 'license.sensor_count')} sensor${_get(d, 'license.sensor_count', 0) > 1 ? 's' : ''}`
            },
        });
    }, [getMaxCoresSensorName]);

    useEffect(() => {
        setLicenseHealth(_get(systemHealth, 'object.health_metrics.license', {}));
        setSufficient(_get(systemHealth, 'object.health_metrics.license.sufficient_data', 0));
        setLicenseIssues(_get(systemHealth, 'object.health_metrics.issues', [])
            .filter(issue => LICENSE_ERROR_TYPES[issue.id]));

        const cmc = _get(systemHealth, 'object.health_metrics.device_uuid');
        if (cmc) {
            fetchTimelines(cmc);
        }
    }, [systemHealth, fetchTimelines]);

    useEffect(() => {
        SystemService.getLicense().then((res) => {
            setLicenseData(res);
        });
    }, []);

    return (
        <Grid container style={{ paddingBottom: 25 }}>
            <Grid item xs={6}>
                <Paper elevation={3}>
                    <Box p={5}>
                        {licensePerms.write && <Button className={classes.buttonRight} variant="contained" aria-label="request" onClick={handleRequestLicense}>Request</Button>}
                        <FormControl fullWidth>
                            <FormLabel>License Key</FormLabel>
                            <Input
                                onChange={handleUpdateKey}
                                type="text"
                                disabled={!licensePerms.admin}
                                value={licenseData.key}
                            />
                        </FormControl>
                        <Grid container>
                            <Grid item xs={7}>
                                <List>
                                    <ListItem>Valid Through:</ListItem>
                                    <ListItem>Gbps:</ListItem>
                                    <ListItem>Per Sensor CPU Cores:</ListItem>
                                    <ListItem>Total CPU Cores:</ListItem>
                                    <ListItem>Default ETPro Intel:</ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={5}>
                                <List>
                                    <ListItem className={clsx({
                                        [classes.error]: haveError('lic_expired'),
                                        [classes.warn]: haveError('lic_expiring'),
                                    })}>
                                        {Moment(licenseData.expiry, 'MMM DD, YYYY')
                                            .format(theme.date.short)}
                                    </ListItem>
                                    <ListItem className={clsx({ [classes.error]: haveError('lic_inv_tp') })}>
                                        {licenseData.gbps}
                                    </ListItem>
                                    <ListItem className={clsx({ [classes.error]: haveError('lic_inv_cores') })}>
                                        {licenseData.cores}
                                    </ListItem>
                                    <ListItem className={clsx({ [classes.error]: haveError('lic_inv_total_cores') })}>
                                        {licenseData.total_cores}
                                    </ListItem>
                                    <ListItem >{licenseData.etpro ? 'Enabled' : 'Disabled'}</ListItem>
                                </List>
                            </Grid>
                        </Grid>
                        {licensePerms.admin && <Box display="flex" justifyContent="flex-end">
                            <Button aria-label="update" variant="contained" color="primary" onClick={handleUpdateLicense} disabled={!licenseData.key}>Update</Button>
                        </Box>}
                    </Box>
                </Paper>
            </Grid>

            {/* license health warnings */}
            <Grid item xs={6}>
                {licenseIssues.map((issue, i) =>
                    <Box key={`${issue.id}_${i}`} className={classes.errorPanel}>
                        {issue.message}
                    </Box>
                )}
                {/* Insufficient data warning */}
                {sufficient === false &&
                    <Box className={classes.infoPanel}>
                       Insufficient license health data collected.
                    </Box>
                }
            </Grid>

            {/* license summary data */}
            <Grid item xs={12}>
                <Box className={classes.panel}>
                    {/* health summary */}
                    <Grid container className={classes.healthGrid} spacing={4}>
                        <Grid item xs={12}>
                            <Typography className={classes.key}>License Health Summary</Typography>
                            <Typography className={classes.value}>
                                <strong> {_get(licenseHealth, 'data_points')}</strong> samples
                                 from
                                <strong> {_get(licenseHealth, 'peak_sensor_count')} </strong >
                                sensor{_get(licenseHealth, 'peak_sensor_count') > 1 ? 's' : ''},
                                aggregated from
                                <strong> {Moment(_get(licenseHealth, 'start')).format(theme.date.short)} </strong>
                                to
                                <strong> {Moment(_get(licenseHealth, 'end')).format(theme.date.short)} </strong>

                            </Typography>
                        </Grid>
                        {/* sensor counts */}
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Avg. Sensor Count</Typography>
                            <Typography className={classes.value}>
                                {_get(licenseHealth, 'avg_sensor_count', 0).toFixed(0)}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Peak Sensor Count</Typography>
                            <Typography className={classes.value}>
                                {_get(licenseHealth, 'peak_sensor_count', 0)}
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Latest Sensor Count</Typography>
                            <Typography className={classes.value}>
                                {_get(licenseHealth, 'latest_sensor_count', 0)}
                            </Typography>
                        </Grid>

                        <Grid item xs={4}>
                            <Typography className={classes.key}>Total Avg Rx</Typography>
                            <Typography className={classes.value}>
                                {TxBitsTipFilter(_get(licenseHealth, 'avg_rx_bytes_per_sec') * 8) + ' '}
                                Total Avg Rx
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Peak Rx</Typography>
                            <Typography className={classes.value}>
                                {TxBitsTipFilter(_get(licenseHealth, 'peak_rx_bytes') * 8) + ' '}
                                Top Sensor: <strong>{_get(licenseHealth, 'peak_rx_bytes_sensor_name')}</strong>
                            </Typography>
                        </Grid>
                        <Grid item xs={4}>
                            <Typography className={classes.key}>Latest Rx</Typography>
                            <Typography className={classes.value}>
                                {TxBitsTipFilter(_get(licenseHealth, 'latest_rx_bytes') * 8) + ' '}
                                Latest Total Rx
                            </Typography>
                        </Grid>
                    </Grid>

                    <Divider className={classes.divider}/>

                    <Grid container spacing={2} direction="column" alignItems="flex-end">
                        <Grid item>
                            <FormLabel style={{ paddingRight: 5 }}>Show thresholds</FormLabel>
                            <ToggleButtonGroup size="small" value={trendlines} exclusive onChange={handleToggleTrendlines}>
                                <ToggleButton aria-label="log scale" value={true}>
                                    Show
                                </ToggleButton>
                                <ToggleButton aria-label="linear scale" value={false}>
                                    Hide
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Grid>
                    </Grid>

                    {/* timelines */}
                    <Paper style={{ marginBottom: 8 }}>

                        {/* throughput */}
                        {throughputHistory.metrics && <LicenseHealthPanel
                            title="Throughput"
                            data={throughputHistory}
                            icon={<MultipleStopIcon/>}
                        />}
                    </Paper>
                    <Paper>
                        {/* capacity */}
                        {capacityHistory.metrics && <LicenseHealthPanel
                            title="Capacity"
                            data={capacityHistory}
                            icon={<SpeedIcon/>}
                        />}
                    </Paper>
                </Box>
            </Grid>
        </Grid>
    );
};

const LicenseHealthPanel = ({ data, title, icon }) => {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);
    const { updateProfile, getProfile, /*perm*/ } = useContext(ProfileContext);

    const onExpand = useCallback(v => {
        setExpanded(!v);
        const profile = getProfile('license');
        updateProfile('license', {
            ...profile,
            panels: { ...profile.panels, [title]: !v },
        });
    }, [title, getProfile, updateProfile]);

    useEffect(() => {
        // read user pofile
        const profile = getProfile('license');
        setExpanded(_get(profile, `panels.${title}`, true));

        console.log(data);
    }, [getProfile, title]);

    return (
        <Accordion
            TransitionProps={{ unmountOnExit: true }}
            expanded={expanded}
            onChange={() => onExpand(expanded)}>
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={title + '-content'}>
                {icon}
                <Typography className={classes.panelHeading}>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.panelDetails}>
                <SparklineGroup
                    metrics={data.metrics}
                    height={sparklineSettings.chartHeight + sparklineSettings.margin}
                    start={data.start}
                    end={data.end}
                    settings={sparklineSettings}>
                </SparklineGroup>
            </AccordionDetails>
        </Accordion>
    );
};
LicenseHealthPanel.propTypes = {
    data: PropTypes.object.isRequired,
    icon: PropTypes.node.isRequired,
    title: PropTypes.string.isRequired,
    uuid: PropTypes.string,
};

export default License;
